import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal, { Overline } from "@/components/Reveal";

const CDN = "https://assets.spotlighthyperlocal.com/assets/3dc7de01-597d-4a2c-9e23-6a70769bacad/raw_creative";

const GALLERY = [
  { src: `${CDN}/0ed85494-792c-45ef-945c-1d2ffc08a112.jpg`, title: "Sorento Showcase", tag: "Flagship SUV" },
  { src: `${CDN}/bae3f83e-70bb-4f70-863f-ca995036e2c8.png`, title: "New Seltos Reveal", tag: "Bestseller" },
  { src: `${CDN}/050f6840-da68-4f69-a492-246ba784452e.png`, title: "Sonet Showcase", tag: "Compact King" },
  { src: `${CDN}/472da389-185e-4a39-9220-45b073858aed.jpg`, title: "Carens Clavis Lounge", tag: "Family MPV" },
  { src: `${CDN}/19cb83f9-2573-4626-8b17-21f46c8373c1.jpg`, title: "Clavis EV Charge-Up", tag: "Electric" },
  { src: `${CDN}/a7b43490-1a1f-4879-ac3a-8e181a434013.jpg`, title: "Syros EV Spotlight", tag: "Electric" },
];

export default function Gallery() {
  const [selected, setSelected] = useState(null);

  const close = () => setSelected(null);
  const prev = (e) => {
    e?.stopPropagation();
    setSelected((s) => (s - 1 + GALLERY.length) % GALLERY.length);
  };
  const next = (e) => {
    e?.stopPropagation();
    setSelected((s) => (s + 1) % GALLERY.length);
  };

  useEffect(() => {
    if (selected === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative bg-[#0A0F14] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Overline>From Our Floor</Overline>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display font-extrabold tracking-tight leading-[1.02] text-3xl sm:text-5xl text-white max-w-xl">
              The Sanjib Kia <span className="text-outline">Showroom.</span>
            </h2>
            <p className="text-slate-400 text-sm sm:text-base max-w-sm leading-relaxed">
              Moments from our NH-20 Panikoili floor — every car prepped, polished and ready for its new family.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={i * 0.07}>
              <figure
                data-testid={`gallery-item-${i}`}
                onClick={() => setSelected(i)}
                className="group relative overflow-hidden clip-card border border-white/10 hover:border-[#FFFFFF]/50 transition-colors duration-500 cursor-pointer"
              >
                <img
                  src={g.src}
                  alt={`${g.title} — Sanjib Kia showroom`}
                  loading="lazy"
                  className="w-full h-60 object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05141F]/90 via-transparent to-transparent" />
                <figcaption className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#FFFFFF]">{g.tag}</p>
                    <p className="font-display font-bold text-white text-base mt-1">{g.title}</p>
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <AnimatePresence>
          {selected !== null && (
            <motion.div
              data-testid="gallery-lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
              onClick={close}
            >
              <button
                data-testid="lightbox-close-button"
                onClick={close}
                className="absolute top-5 right-5 w-11 h-11 grid place-items-center bg-white/10 border border-white/20 text-white hover:border-[#FFFFFF] transition-colors z-10"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <button
                data-testid="lightbox-prev-button"
                onClick={prev}
                className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-white/10 border border-white/20 text-white hover:border-[#FFFFFF] transition-colors z-10"
                aria-label="Previous photo"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                data-testid="lightbox-next-button"
                onClick={next}
                className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-white/10 border border-white/20 text-white hover:border-[#FFFFFF] transition-colors z-10"
                aria-label="Next photo"
              >
                <ChevronRight size={20} />
              </button>
              <motion.figure
                key={selected}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-4xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  data-testid="lightbox-image"
                  src={GALLERY[selected].src}
                  alt={GALLERY[selected].title}
                  className="w-full max-h-[75vh] object-contain clip-card"
                />
                <figcaption className="mt-4 flex items-center justify-between">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFFFFF]">{GALLERY[selected].tag}</p>
                    <p data-testid="lightbox-title" className="font-display font-bold text-white text-lg mt-1">{GALLERY[selected].title}</p>
                  </div>
                  <span className="font-mono text-xs text-slate-500">{selected + 1} / {GALLERY.length}</span>
                </figcaption>
              </motion.figure>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
